<template>
    <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
            <div :class="`mb-3 flex ${columnWidth} justify-between`">
                <span class="pl-5 font-bold text-primary">{{ totalLabel }}</span>
                <span class="font-bold text-primary">{{ formatCurrency(total, currency) }}</span>
            </div>
            <ul
                class="ml-0 flex list-none flex-col gap-y-1 text-primary lg:grid lg:auto-cols-auto lg:grid-flow-col lg:grid-rows-4 lg:gap-x-10">
                <li v-for="(item, index) in items" :key="`item-${index}`">
                    <div :class="`flex ${columnWidth} justify-between`">
                        <p
                            :class="{
                                'truncate whitespace-nowrap': true,
                                'font-semibold': selectedLabel === item.name,
                            }"
                            :title="`${item.name}: ${formatCurrency(item.amount, currency)}`">
                            <span
                                class="mr-2 inline-block h-3 w-3"
                                :style="{ 'background': chartColors[index] }"></span>
                            <span v-if="item.name === 'other'">{{ $t('other') }}</span>
                            <span v-else>{{ item.name }}</span>
                        </p>
                        <span
                            :class="{
                                'ml-3 whitespace-nowrap': true,
                                'font-semibold': selectedLabel === item.name,
                            }"
                            :title="`${item.name}: ${formatCurrency(item.amount, currency)}`"
                            >{{ formatCurrency(item.amount, currency) }}</span
                        >
                    </div>
                </li>
            </ul>
        </div>
        <div class="w-full md:w-40">
            <DoughnutChart v-bind="doughnutChartProps" @mouseleave="() => (selectedLabel = '')" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { DoughnutChart, useDoughnutChart } from 'vue-chart-3'
import { Chart, ChartData, registerables } from 'chart.js'
import { computed, ref, toRefs } from 'vue'
import { formatCurrency } from '../../utilities/helpers'
import { chartColors } from './const/chartColors'

export interface ChartItem {
    name: string
    amount: number
}

const props = defineProps<{
    total: number
    totalLabel: string
    currency: string
    items: ChartItem[]
}>()

Chart.register(...registerables)

const { total, items } = toRefs(props)

const columnWidth = computed(() => {
    if (items.value.length > 3) {
        return 'lg:w-60'
    }
    return 'lg:w-96'
})

const labels = items.value.map((item) => item.name)
const chartData = computed<ChartData<'doughnut'>>(() => ({
    labels: labels,
    datasets: [
        {
            data: items.value.map((item) => (item.amount / total.value) * 100),
            backgroundColor: chartColors.slice(0, items.value.length),
        },
    ],
}))
const options = computed(() => ({
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            external: externalTooltipHandler,
        },
    },
}))
const { doughnutChartProps } = useDoughnutChart({
    chartData,
    options,
})

const selectedLabel = ref<string>('')

const externalTooltipHandler = (context) => {
    const { tooltip } = context
    const labelTruncate =
        tooltip.dataPoints[0].label.length >= 16
            ? `${tooltip.dataPoints[0].label.slice(0, 16)}...`
            : tooltip.dataPoints[0].label
    tooltip.body[0].lines[0] = `${labelTruncate} ${Math.round(tooltip.dataPoints[0].formattedValue)}%`
    selectedLabel.value = tooltip.dataPoints[0].label
}
</script>

<style scoped></style>
